/**
 * PageContext.js
 * Lets any page tell the chatbot about itself. The chatbot lives in <Layout>
 * (a sibling of the page), so the page registers a stable "handle" here and
 * the chatbot reads it through usePageContext().
 *
 * Page usage:
 *   useChatbotPage({ id, title, description, apis, fields,
 *                    getValues, setValues, save, validate?, mapSpec? });
 */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const Ctx = createContext({ page: null, register: () => () => {}, pathname: "" });

export const ChatbotPageProvider = ({ children }) => {
  const [page, setPage] = useState(null);
  const { pathname } = useLocation();
  const value = useMemo(
    () => ({
      page,
      pathname,
      register: (handle) => {
        setPage(handle);
        return () => setPage((p) => (p === handle ? null : p));
      },
    }),
    [page, pathname]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const usePageContext = () => useContext(Ctx);

export const useChatbotPage = (config) => {
  const { register } = useContext(Ctx);
  const latest = useRef(config);
  latest.current = config; // callbacks always see the freshest page state

  useEffect(() => {
    const c = latest.current;
    const handle = {
      id: c.id,
      title: c.title,
      description: c.description,
      apis: c.apis,
      fields: c.fields,
      getValues: () => latest.current.getValues(),
      setValues: (v) => latest.current.setValues(v),
      save: () => latest.current.save(),
      validate: latest.current.validate ? (v) => latest.current.validate(v) : null,
      mapSpec: latest.current.mapSpec ? (s) => latest.current.mapSpec(s) : null,
    };
    return register(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.id]);
};
